// Draws gate A1.3's test scene: the heightfield as a flat-shaded mesh, every other body as an instance of its shape,
// posed from the collision world in the `late` phase. View only: it reads the sim and never writes it.
import * as THREE from "three";
import { ConvexGeometry } from "three/examples/jsm/geometries/ConvexGeometry.js";
import type { BodySpec, CollisionWorld, ShapeSpec } from "@rpg/collision";

const COLOURS = { fixed: "#8d8577", ground: "#7fa955", prop: ["#d9a441", "#c8693c", "#5f8fb4", "#b7c4cf"], pusher: "#ad2c25" };

function geometryOf(s: ShapeSpec): THREE.BufferGeometry {
  switch (s.type) {
    case "cuboid":
      return new THREE.BoxGeometry(s.halfExtents.x * 2, s.halfExtents.y * 2, s.halfExtents.z * 2);
    case "ball":
      return new THREE.IcosahedronGeometry(s.radius, 1);
    case "capsule":
      return new THREE.CapsuleGeometry(s.radius, s.halfHeight * 2, 3, 8);
    case "cylinder":
      return new THREE.CylinderGeometry(s.radius, s.radius, s.halfHeight * 2, 10);
    case "trimesh": {
      const g = new THREE.BufferGeometry();
      g.setAttribute("position", new THREE.Float32BufferAttribute(Array.from(s.vertices), 3));
      g.setIndex(Array.from(s.indices));
      const flat = g.toNonIndexed();
      flat.computeVertexNormals();
      return flat;
    }
    case "heightfield": {
      const g = new THREE.PlaneGeometry(s.size.x, s.size.z, s.cols, s.rows).rotateX(-Math.PI / 2);
      const p = g.attributes.position!;
      // PlaneGeometry rotated flat runs its rows from -z, as the heightfield's samples do
      for (let i = 0; i < p.count; i++) p.setY(i, s.heights[i]! * s.size.y);
      g.computeVertexNormals();
      return g;
    }
    case "convex": {
      // phase C5: a convex hull (a breakable's fragment), drawn flat
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i + 2 < s.points.length; i += 3) pts.push(new THREE.Vector3(s.points[i], s.points[i + 1], s.points[i + 2]));
      return new ConvexGeometry(pts);
    }
  }
}

/** Build the scene's meshes under `parent`; returns the per-frame pose update. */
export function createSimView(parent: THREE.Object3D, bodies: readonly BodySpec[], collision: CollisionWorld, colourOf?: (b: BodySpec, i: number) => string | undefined): () => void {
  const group = new THREE.Group();
  group.name = "sim-test-scene";
  parent.add(group);
  const sun = new THREE.DirectionalLight("#fff1d0", 1.4);
  sun.position.set(8, 14, 6);
  group.add(sun);

  // one instanced mesh per distinct shape and colour
  const groups = new Map<string, { geo: THREE.BufferGeometry; colour: string; ids: string[] }>();
  bodies.forEach((b, i) => {
    const shape = b.colliders[0]!.shape;
    const colour = colourOf?.(b, i) ?? (b.kind === "fixed" ? (shape.type === "heightfield" ? COLOURS.ground : COLOURS.fixed) : b.kind === "kinematic" ? COLOURS.pusher : COLOURS.prop[i % 4]!);
    const key = shape.type === "heightfield" || shape.type === "trimesh" ? `${b.id}` : `${JSON.stringify(shape)}|${colour}`;
    let g = groups.get(key);
    if (!g) groups.set(key, (g = { geo: geometryOf(shape), colour, ids: [] }));
    g.ids.push(b.id);
  });
  const meshes: { mesh: THREE.InstancedMesh; ids: string[] }[] = [];
  for (const g of groups.values()) {
    const mesh = new THREE.InstancedMesh(g.geo, new THREE.MeshLambertMaterial({ color: g.colour, flatShading: true }), g.ids.length);
    mesh.frustumCulled = false;
    mesh.name = g.ids.length === 1 ? `body:${g.ids[0]}` : "bodies";
    group.add(mesh);
    meshes.push({ mesh, ids: g.ids });
  }

  const m = new THREE.Matrix4();
  const pos = new THREE.Vector3();
  const rot = new THREE.Quaternion();
  const one = new THREE.Vector3(1, 1, 1);
  const update = (): void => {
    for (const { mesh, ids } of meshes) {
      ids.forEach((id, i) => {
        if (!collision.has(id)) return;
        const p = collision.pose(id);
        mesh.setMatrixAt(i, m.compose(pos.set(p.position.x, p.position.y, p.position.z), rot.set(p.rotation.x, p.rotation.y, p.rotation.z, p.rotation.w), one));
      });
      mesh.instanceMatrix.needsUpdate = true;
    }
  };
  update();
  return update;
}
